import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Order, OrderSummary } from '@entities/order'
import { convertUtcToMoscowTime } from '@shared/lib'
import { PAYMENT_STATUSES } from '@shared/config'

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000

export const useUnpaidOrdersStore = defineStore('unpaidOrders', () => {
  const nuxtApp = useNuxtApp()
  const logger = (nuxtApp.$logger as any) || console

  // State
  const summaries = ref<OrderSummary[]>([])
  const detailsCache = ref<Record<string, Order>>({})
  const selectedOrderId = ref<string | null>(null)

  // Getters
  const selectedSummary = computed<OrderSummary | null>(() => {
    const found = summaries.value.find(s => s.id === selectedOrderId.value)
    return found || null
  })

  const selectedDetails = computed<Order | null>(() => {
    if (!selectedSummary.value) return null
    return detailsCache.value[selectedSummary.value.id] || null
  })

  const selectedOrder = computed<Order | null>(() => {
    if (!selectedSummary.value) return null

    const details = detailsCache.value[selectedSummary.value.id]
    if (details) return details

    return {
      id: selectedSummary.value.id,
      status: selectedSummary.value.status,
      paymentStatus: selectedSummary.value.paymentStatus,
      createdAt: selectedSummary.value.createdAt,
      updatedAt: selectedSummary.value.updatedAt,
      externalPaymentId: selectedSummary.value.externalPaymentId,
    } as Order
  })

  const hasUnpaidOrders = computed(() => summaries.value.length > 0)
  const count = computed(() => summaries.value.length)

  const unpaidOrders = computed(() =>
    summaries.value.filter(order => order.paymentStatus !== 'paid'),
  )

  const paidOrders = computed(() =>
    summaries.value.filter(order => order.paymentStatus === 'paid'),
  )

  const unpaidCount = computed(() => unpaidOrders.value.length)
  const paidCount = computed(() => paidOrders.value.length)

  const selectedOrderIndex = computed(() => {
    if (!selectedSummary.value) return -1
    return summaries.value.findIndex(s => s.id === selectedSummary.value?.id)
  })

  const allSummaries = computed(() => summaries.value)

  // Private helpers
  function filterExpiredOrders(orders: OrderSummary[]): OrderSummary[] {
    const now = Date.now()
    const fifteenMinutesAgo = now - FIFTEEN_MINUTES_MS

    return orders.filter(order => {
      const status = order.paymentStatus

      // Показываем все статусы оплаты, кроме «ожидает оплаты» и «обработка платежа» после истечения времени
      const isAlwaysVisible = status !== PAYMENT_STATUSES.WAITING && status !== PAYMENT_STATUSES.PENDING
      if (isAlwaysVisible) {
        return true
      }

      // Для статусов ожидания/обработки проверяем таймаут
      const orderCreatedAt = new Date(order.createdAt).getTime()
      const isValid = orderCreatedAt > fifteenMinutesAgo

      if (!isValid) {
        delete detailsCache.value[order.id]
      }

      return isValid
    })
  }

  function sortOrders(orders: OrderSummary[]): OrderSummary[] {
    return [...orders].sort((a, b) => {
      const aIsPaid = a.paymentStatus === 'paid'
      const bIsPaid = b.paymentStatus === 'paid'

      if (!aIsPaid && bIsPaid) return -1
      if (aIsPaid && !bIsPaid) return 1

      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  }

  function normalizeTime(dateStr: string): string {
    return dateStr.includes('+03:00') ? dateStr : convertUtcToMoscowTime(dateStr)
  }

  // Actions
  function setSummaries(newSummaries: OrderSummary[]): void {
    const normalized = newSummaries.map(s => ({
      ...s,
      createdAt: normalizeTime(s.createdAt),
      updatedAt: normalizeTime(s.updatedAt),
    }))

    const valid = filterExpiredOrders(normalized)
    const sorted = sortOrders(valid)

    summaries.value = sorted

    // ✅ ИСПРАВЛЕНИЕ: Обновляем detailsCache для заказов, которые уже в кэше
    // Это необходимо, чтобы модальное окно получало актуальные статусы
    for (const summary of sorted) {
      if (detailsCache.value[summary.id]) {
        logger.info?.(`[setSummaries] Updating cached details for order ${summary.id}`, {
          oldStatus: detailsCache.value[summary.id].status,
          newStatus: summary.status,
          oldPaymentStatus: detailsCache.value[summary.id].paymentStatus,
          newPaymentStatus: summary.paymentStatus,
        })

        // Обновляем только статусы и временные метки, сохраняя полные данные (items и т.д.)
        detailsCache.value[summary.id] = {
          ...detailsCache.value[summary.id],
          status: summary.status,
          paymentStatus: summary.paymentStatus,
          updatedAt: summary.updatedAt,
          externalPaymentId: summary.externalPaymentId,
        }
      }
    }

    if (sorted.length > 0) {
      const hasSelected = selectedOrderId.value &&
        sorted.some(s => s.id === selectedOrderId.value)

      if (!hasSelected) {
        selectedOrderId.value = sorted[0].id
      }
    } else {
      selectedOrderId.value = null
    }
  }

  function addSummary(summary: OrderSummary): void {
    const normalized: OrderSummary = {
      ...summary,
      createdAt: normalizeTime(summary.createdAt),
      updatedAt: normalizeTime(summary.updatedAt),
    }

    const existingIndex = summaries.value.findIndex(s => s.id === normalized.id)

    if (existingIndex === -1) {
      summaries.value.unshift(normalized)
    } else {
      summaries.value[existingIndex] = normalized
    }

    // ✅ ИСПРАВЛЕНИЕ: Обновляем detailsCache если заказ уже в кэше
    if (detailsCache.value[normalized.id]) {
      logger.info?.(`[addSummary] Updating cached details for order ${normalized.id}`, {
        oldStatus: detailsCache.value[normalized.id].status,
        newStatus: normalized.status,
        oldPaymentStatus: detailsCache.value[normalized.id].paymentStatus,
        newPaymentStatus: normalized.paymentStatus,
      })

      detailsCache.value[normalized.id] = {
        ...detailsCache.value[normalized.id],
        status: normalized.status,
        paymentStatus: normalized.paymentStatus,
        updatedAt: normalized.updatedAt,
        externalPaymentId: normalized.externalPaymentId,
      }
    }

    selectedOrderId.value = normalized.id
  }

  async function loadOrderDetails(orderId: string): Promise<Order | null> {
    if (detailsCache.value[orderId]) {
      return detailsCache.value[orderId]
    }

    try {
      const apiProfileInstance = (await import('~/entities/user')).default
      const order: Order = await apiProfileInstance.getOrderDetails(orderId)

      const normalizedOrder: Order = {
        ...order,
        createdAt: normalizeTime(order.createdAt),
        updatedAt: normalizeTime(order.updatedAt),
      }

      detailsCache.value[orderId] = normalizedOrder
      return normalizedOrder
    } catch (error) {
      logger.error('[UnpaidOrdersStore] Failed to load order details:', error)
      return null
    }
  }

  function selectOrder(orderId: string): void {
    const found = summaries.value.find(s => s.id === orderId)
    if (found) {
      selectedOrderId.value = orderId
    }
  }

  function removeOrder(orderId: string): void {
    summaries.value = summaries.value.filter(s => s.id !== orderId)
    delete detailsCache.value[orderId]

    if (selectedOrderId.value === orderId) {
      selectedOrderId.value = summaries.value.length > 0 ? summaries.value[0].id : null
    }
  }

  function updateSummary(orderId: string, updates: Partial<OrderSummary>): void {
    const index = summaries.value.findIndex(s => s.id === orderId)
    if (index !== -1) {
      summaries.value[index] = {
        ...summaries.value[index],
        ...updates,
        updatedAt: normalizeTime(updates.updatedAt || new Date().toISOString()),
      }

      if (detailsCache.value[orderId]) {
        const cached = detailsCache.value[orderId]
        detailsCache.value[orderId] = { ...cached, ...updates }
      }
    }
  }

  function clearAll(): void {
    summaries.value = []
    detailsCache.value = {}
    selectedOrderId.value = null
  }

  function getSummaryById(orderId: string): OrderSummary | null {
    return summaries.value.find(s => s.id === orderId) || null
  }

  function hasDetails(orderId: string): boolean {
    return !!detailsCache.value[orderId]
  }

  function getDetailsById(orderId: string): Order | null {
    return detailsCache.value[orderId] || null
  }

  return {
    // State (read-only)
    summaries: computed(() => summaries.value),
    selectedOrderId: computed(() => selectedOrderId.value),

    // Getters
    selectedSummary,
    selectedDetails,
    selectedOrder,
    hasUnpaidOrders,
    count,
    selectedOrderIndex,
    allSummaries,
    unpaidOrders,
    paidOrders,
    unpaidCount,
    paidCount,

    // Actions
    setSummaries,
    addSummary,
    loadOrderDetails,
    selectOrder,
    removeOrder,
    updateSummary,
    clearAll,
    getSummaryById,
    hasDetails,
    getDetailsById,
  }
})
